import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import {
  Users, Clock, Calendar, Mic, ChevronDown,
  ChevronUp, BarChart2, CheckCircle2, XCircle
} from "lucide-react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import { Button } from "@/clientWeb/components/ui/button"
import { NoData } from "@/clientWeb/components/ui/no-data"
import nodataAnalyticsImg from "@/assets/nodataanalytics.svg"

const COLORS = ["#0ea5e9", "#8b5cf6", "#f97316", "#10b981", "#ec4899"]

interface Participant {
  id: string
  name: string
  email: string
  role: "Host" | "Participant"
  joinTime: string
  duration: string
  device: "Desktop" | "Mobile" | "Tablet"
  hasVideo: boolean
  hasAudio: boolean
}

interface Poll {
  id: string
  question: string
  submittedUsers: number
  options: { label: string; votes: number }[]
}

// No analytics API yet (see services/analytics.repo.ts), so nothing is known
// about this meeting. Every meeting id used to show the same invented session
// ("Product Review Session", Apr 17 2024) with five made-up participants and
// three polls.
const participants: Participant[] = []
const polls: Poll[] = []

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.fill }} className="mt-0.5">{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  )
}

export default function MeetingDetail() {
  const t = useTranslation()
  const navigate = useNavigate()
  const { meetingId } = useParams()
  const [openPolls, setOpenPolls] = useState<string[]>([])

  const togglePoll = (id: string) => {
    setOpenPolls(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id])
  }

  const noData = <NoData image={nodataAnalyticsImg} title={t("analytics.noDataAvailable")} size="sm" showActionIcon={false} />

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-background p-4 md:p-6 gap-5">
      {/* Breadcrumb */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/analytics")} className="text-primary font-semibold transition-colors flex items-center gap-1">
            <BarChart2 className="w-3.5 h-3.5 text-primary" /> {t("analytics.title")}
          </button>
          <span>/</span>
          <button onClick={() => navigate("/analytics/meetings")} className="text-primary font-semibold transition-colors">{t("meetings.title")}</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-medium">{t("meetings.meetingDetails")}</span>
        </div>

        <div className="flex justify-between items-center gap-3">
          {/* <button onClick={() => navigate("/analytics/meetings")}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button> */}
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("meetings.meetingDetails")}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{t("meetings.meetingId")}: {meetingId}</p>
          </div>
          <Button>{t("common.download")}</Button>
        </div>
      </div>

      {/* Meeting Info Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t("meetings.date"), value: "—", icon: <Calendar className="w-4 h-4 text-blue-500" />, bg: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20" },
          { label: t("analytics.duration"), value: "—", icon: <Clock className="w-4 h-4 text-emerald-500" />, bg: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20" },
          { label: t("common.participants"), value: "—", icon: <Users className="w-4 h-4 text-purple-500" />, bg: "bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20" },
          { label: t("analytics.recording"), value: "—", icon: <Mic className="w-4 h-4 text-amber-500" />, bg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20" },
        ].map(c => (
          <div key={c.label} className="relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-4 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden">
            {/* Decorative Background Wave */}
            <div className={`absolute inset-0 ${c.bg}`}>
              <svg 
                className="absolute bottom-0 right-0 w-full h-full" 
                viewBox="0 0 200 120" 
                fill="none" 
                preserveAspectRatio="none"
              >
                <path 
                  d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                  fill="currentColor" 
                  className="text-white/30 dark:text-white/10"
                />
              </svg>
            </div>

            {/* Content aligned like PaymentHistory cards */}
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-card/50 dark:bg-card/10 flex items-center justify-center shrink-0">
                {c.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-md font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none truncate">
                  {c.value}
                </div>
                <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mt-1 truncate">
                  {c.label}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Participants Table */}
        <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("common.participants")}</h3>
            <span className="text-xs text-slate-400">{participants.length} {t("analytics.users")}</span>
          </div>
          {participants.length === 0 ? noData : (
          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="border-b border-stroke-100 dark:border-border dark:border-border">
                  {[t("common.name"), t("profile.role"), t("analytics.joined"), t("analytics.duration"), t("analytics.device"), t("analytics.av")].map(h => (
                    <TableHead key={h} className="text-left py-2 pr-3 text-slate-500 dark:text-slate-400 font-medium">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {participants.map(p => (
                  <TableRow key={p.id} className="border-b border-slate-50 dark:border-border/40 hover:bg-slate-50 dark:hover:bg-slate-700/20">
                    <TableCell className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                          {p.name[0]}
                        </div>
                        <span className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[80px]">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 pr-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        p.role === "Host" ? "bg-primary/10 text-primary" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                      }`}>{p.role}</span>
                    </TableCell>
                    <TableCell className="py-2.5 pr-3 text-slate-500 dark:text-slate-400">{p.joinTime}</TableCell>
                    <TableCell className="py-2.5 pr-3 text-slate-500 dark:text-slate-400">{p.duration}</TableCell>
                    <TableCell className="py-2.5 pr-3 text-slate-500 dark:text-slate-400">{p.device}</TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex items-center gap-1">
                        {p.hasVideo
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                          : <XCircle className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />}
                        {p.hasAudio
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                          : <XCircle className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </div>

        {/* Duration Chart */}
        <div className="bg-card flex flex-col dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-xl p-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-1">{t("analytics.participantTimeSpent")}</h3>
          <p className="text-xs text-slate-400 mb-4">{t("analytics.durationInMinutes")}</p>
          <div className="flex flex-1">
            {participants.length === 0 ? noData : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={participants.map(p => ({ name: p.name.split(" ")[0], minutes: parseInt(p.duration) }))}
                margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="name" fontSize={11} stroke="currentColor" />
                <YAxis fontSize={11} stroke="currentColor" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="minutes" name={t("analytics.minutes")} radius={[4, 4, 0, 0]}>
                  {participants.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Polls Section */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.pollResults")}</h3>
          <span className="text-xs text-slate-400">{polls.length} {t("analytics.polls")}</span>
        </div>

        {polls.length === 0 ? noData : (
          <div className="space-y-3">
            {polls.map((poll, idx) => (
              <div key={poll.id} className="border border-stroke-100 dark:border-border dark:border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => togglePoll(poll.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors text-left">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold shrink-0">
                    Q{idx + 1}
                  </div>
                  <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-200">{poll.question}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-slate-500 dark:text-slate-400">{poll.submittedUsers} users</span>
                    {openPolls.includes(poll.id)
                      ? <ChevronUp className="w-4 h-4 text-slate-400" />
                      : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {openPolls.includes(poll.id) && (
                  <div className="px-4 pb-4 border-t border-stroke-100 dark:border-border dark:border-border">
                    <div className="h-[140px] mt-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={poll.options} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={28}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                          <XAxis dataKey="label" fontSize={10} stroke="currentColor" />
                          <YAxis fontSize={10} stroke="currentColor" allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="votes" name="Votes" radius={[4, 4, 0, 0]}>
                            {poll.options.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {poll.options.map((o, i) => (
                        <div key={o.label} className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-slate-500 dark:text-slate-400">{o.label}: <strong className="text-slate-800 dark:text-white">{o.votes}</strong></span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
